import { Alert, AlertDescription, AlertIcon, AlertTitle, Box, Button, ButtonGroup, Center, Flex, IconButton, Input, RangeSlider, RangeSliderFilledTrack, RangeSliderThumb, RangeSliderTrack, Slider, SliderFilledTrack, SliderThumb, SliderTrack, Text } from "@chakra-ui/react"
import { faArrowLeft, faArrowRight, faCancel, faPause, faPlay, faUpload } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { PixelCoordinate } from "@xnav/types/src/common"
import { useEffect, useRef, useState } from "react"
import { RenderJob } from "./addMediaButton"
import { v4 } from 'uuid'
import { MediaMeta, MediaType } from "@/pages/mediaManager"

const VideoTrimmer: React.FC<{
	video: File,
	onSubmit: (job: RenderJob) => void
	onValidate: (newMedia: MediaMeta) => string | undefined
}> = ({ video, onSubmit, onValidate }) => {
	const [videoTime, setVideoTime] = useState(0)
	const [videoDuration, setVideoDuration] = useState(0)
	const [videoPaused, setVideoPaused] = useState(true)
	const [clientScale, setClientScale] = useState(1)
	const [dragOriginCoords, setDragOriginCoords] = useState<PixelCoordinate>({ x: 0, y: 0 })
	const [currentDragPoint, setCurrentDragPoint] = useState<number>()
	const [invalid, setInvalid] = useState<string>()

	function makeNewMedia(): MediaMeta {
		const id = v4()
		return {
			id: `${id}.mp4`,
			displayMode: 'cover',
			iconId: `${id}.png`,
			type: MediaType.Video,
			size: video.size,
			uploaded: new Date(),
			name: video.name.slice(0, video.name.lastIndexOf('.')),
			references: [],
			fileUrl: URL.createObjectURL(video),
			iconUrl: 'placeholder',
		}
	}

	const [newMedia, setNewMedia] = useState<MediaMeta>(makeNewMedia())

	const [cropBox, setCropBox] = useState({ x: 0, y: 0, width: 0, height: 0 })
	const [trimTime, setTrimTime] = useState({ start: 0, end: 0 })
	const [mode, setMode] = useState<'contain' | 'cover'>('cover')

	const videoRef = useRef<HTMLVideoElement>(null)
	const canvasRef = useRef<HTMLCanvasElement>()
	const contextRef = useRef<CanvasRenderingContext2D>()

	useEffect(() => {
		setInvalid(onValidate(newMedia))
	}, [newMedia])

	function handleCanvasRef(newCanvasRef: HTMLCanvasElement) {
		if (!newCanvasRef) return
		const newContextRef = newCanvasRef.getContext('2d')!
		canvasRef.current = newCanvasRef
		contextRef.current = newContextRef
	}

	function handleVideoMetaLoad() {
		const vidRef = videoRef.current!
		setVideoTime(0)
		setVideoDuration(vidRef.duration)
		setTrimTime({ start: 0, end: vidRef.duration })
		setClientScale(vidRef.videoWidth / vidRef.clientWidth)
		const width = vidRef.clientWidth
		const height = vidRef.clientHeight
		canvasRef.current!.width = width
		canvasRef.current!.height = height
		if (height / width < 2 / 3) {
			// narrower
			const cropWidth = (3 / 2) * height
			setCropBox({ x: width / 2 - cropWidth / 2, y: 0, width: cropWidth, height: height })
		} else {
			// Taller
			const cropHeight = (2 / 3) * width
			setCropBox({ x: 0, y: height / 2 - cropHeight / 2, width: width, height: cropHeight })
		}
	}

	function drawCircle(context: CanvasRenderingContext2D, x: number, y: number, radius: number) {
		context.beginPath()
		context.arc(x, y, radius, 0, 2 * Math.PI)
		context.fill()
	}

	function drawCropBox() {
		if (!canvasRef.current || !contextRef.current) return
		const canvas = canvasRef.current
		const context = contextRef.current
		const width = canvas.width
		const height = canvas.height
		context.clearRect(0, 0, width, height)

		if (mode === 'contain') return // Don't draw box in contain mode

		context.fillStyle = '#00000099'
		context.strokeStyle = 'white'
		context.lineWidth = 2
		context.fillRect(0, 0, width, height)
		context.clearRect(cropBox.x, cropBox.y, cropBox.width, cropBox.height)
		context.strokeRect(cropBox.x, cropBox.y, cropBox.width, cropBox.height)
		context.fillStyle = 'white'
		drawCircle(context, cropBox.x, cropBox.y, 10)
		drawCircle(context, cropBox.x, cropBox.y + cropBox.height, 10)
		drawCircle(context, cropBox.x + cropBox.width, cropBox.y, 10)
		drawCircle(context, cropBox.x + cropBox.width, cropBox.y + cropBox.height, 10)
	}

	function shrinkToAspect(box: PixelCoordinate, aspect: PixelCoordinate = { x: 3, y: 2 }): PixelCoordinate {
		if (box.x < 50) box.x = 50
		if (box.y < 50) box.x = 50
		if (box.x / box.y > aspect.x / aspect.y) return { x: (aspect.x / aspect.y) * box.y, y: box.y }
		return { x: box.x, y: (aspect.y / aspect.x) * box.x }
	}

	function onCanvasClick(e: React.MouseEvent<HTMLCanvasElement>) {
		if (mode === 'contain') return
		function isClickingCircle(click: PixelCoordinate, circle: PixelCoordinate, radius: number): boolean {
			return click.x > circle.x - radius && click.x < circle.x + radius && click.y > circle.y - radius && click.y < circle.y + radius
		}
		if (!canvasRef.current || !contextRef.current) return
		const bounding = canvasRef.current.getBoundingClientRect()
		const x = e.clientX - bounding.left
		const y = e.clientY - bounding.top
		setDragOriginCoords({ x: x - cropBox.x, y: y - cropBox.y })
		if (isClickingCircle({ x: x, y: y }, cropBox as PixelCoordinate, 10)) setCurrentDragPoint(1)
		else if (isClickingCircle({ x: x, y: y }, { x: cropBox.x + cropBox.width, y: cropBox.y }, 10))
			setCurrentDragPoint(2)
		else if (isClickingCircle({ x: x, y: y }, { x: cropBox.x, y: cropBox.y + cropBox.width }, 10))
			setCurrentDragPoint(3)
		else if (isClickingCircle({ x: x, y: y }, { x: cropBox.x + cropBox.width, y: cropBox.y + cropBox.height }, 10))
			setCurrentDragPoint(4)
		else setCurrentDragPoint(0)
	}

	function onCanvasDrag(e: React.MouseEvent<HTMLCanvasElement>) {
		if (!dragOriginCoords || !canvasRef.current || !contextRef.current) return
		const bounding = canvasRef.current.getBoundingClientRect()
		const newX = e.clientX - bounding.left
		const newY = e.clientY - bounding.top
		if (currentDragPoint == 0) {
			let x = newX - dragOriginCoords.x
			let y = newY - dragOriginCoords.y
			if (x < 0) x = 0
			if (y < 0) y = 0
			const xOverflow = x + cropBox.width - canvasRef.current.width
			const yOverflow = y + cropBox.height - canvasRef.current.height
			if (xOverflow > 0) x -= xOverflow
			if (yOverflow > 0) y -= yOverflow
			setCropBox({ x: x, y: y, width: cropBox.width, height: cropBox.height })
		} else if (currentDragPoint == 1) {
			const bottomRight: PixelCoordinate = {
				x: cropBox.x + cropBox.width,
				y: cropBox.y + cropBox.height,
			}
			const topLeft: PixelCoordinate = { x: newX, y: newY }
			const boxMaxSize: PixelCoordinate = { x: bottomRight.x - topLeft.x, y: bottomRight.y - topLeft.y }
			const boxSize = shrinkToAspect(boxMaxSize)
			setCropBox({ x: bottomRight.x - boxSize.x, y: bottomRight.y - boxSize.y, width: boxSize.x, height: boxSize.y })
		} else if (currentDragPoint == 2) {
			const topLeft: PixelCoordinate = { x: cropBox.x, y: newY }
			const bottomRight: PixelCoordinate = { x: newX, y: cropBox.y + cropBox.height }
			const boxMaxSize: PixelCoordinate = { x: bottomRight.x - topLeft.x, y: bottomRight.y - topLeft.y }
			const boxSize = shrinkToAspect(boxMaxSize)
			setCropBox({ x: topLeft.x, y: bottomRight.y - boxSize.y, width: boxSize.x, height: boxSize.y })
		} else if (currentDragPoint == 3) {
			const topLeft: PixelCoordinate = { x: newX, y: cropBox.y }
			const bottomRight: PixelCoordinate = { x: cropBox.x + cropBox.width, y: newY }
			const boxMaxSize: PixelCoordinate = { x: bottomRight.x - topLeft.x, y: bottomRight.y - topLeft.y }
			const boxSize = shrinkToAspect(boxMaxSize)
			setCropBox({ x: bottomRight.x - boxSize.x, y: topLeft.y, width: boxSize.x, height: boxSize.y })
		} else if (currentDragPoint == 4) {
			const topLeft = { x: cropBox.x, y: cropBox.y }
			const bottomRight: PixelCoordinate = { x: newX, y: newY }
			const boxMaxSize: PixelCoordinate = { x: bottomRight.x - topLeft.x, y: bottomRight.y - topLeft.y }
			const boxSize = shrinkToAspect(boxMaxSize)
			setCropBox({ x: topLeft.x, y: topLeft.y, width: boxSize.x, height: boxSize.y })
		}
	}

	useEffect(() => {
		setNewMedia(makeNewMedia())
	}, [video])

	useEffect(() => {
		if (!videoRef.current) return
		if (videoPaused) videoRef.current.pause()
		else videoRef.current.play()
	}, [videoPaused])

	useEffect(() => {
		if (!videoRef.current) return
		const vidRef = videoRef.current!
		if (vidRef.paused) vidRef.currentTime = videoTime
	}, [videoTime])

	useEffect(() => {
		drawCropBox()
	}, [cropBox, mode])

	useEffect(() => {
		function resizeCanvas() {
			if (canvasRef.current && videoRef.current && cropBox) {
				const scaleX = videoRef.current.clientWidth / canvasRef.current.width
				const scaleY = videoRef.current.clientHeight / canvasRef.current.height
				setCropBox({ x: cropBox.x * scaleX, y: cropBox.y * scaleY, width: cropBox.width * scaleX, height: cropBox.height * scaleY })
				canvasRef.current.width = videoRef.current.clientWidth
				canvasRef.current.height = videoRef.current.clientHeight
				setClientScale(videoRef.current.videoWidth / videoRef.current.clientWidth)
			}
		}

		window.addEventListener('resize', resizeCanvas)
		return () => window.removeEventListener('resize', resizeCanvas)
	})

	return (
		<Flex flexDir='column'>
			<canvas
				ref={handleCanvasRef}
				style={{ position: 'absolute', zIndex: 1000 }}
				onMouseDown={onCanvasClick}
				onMouseMove={onCanvasDrag}
				onMouseUp={() => setCurrentDragPoint(undefined)}
			/>
			<video
				ref={videoRef}
				style={{ width: '100%' }}
				src={newMedia.fileUrl}
				onLoadedMetadata={handleVideoMetaLoad}
				onTimeUpdate={(e) => { if (!videoPaused) setVideoTime(e.currentTarget.currentTime) }}
			/>
			<Center gap={10}>
				<Center flexDir='row' minW={170} maxW={170}>
					{videoPaused ? (
						<IconButton
							variant='inline'
							aria-label="Play Video"
							icon={<FontAwesomeIcon icon={faPlay} />}
							onClick={() => setVideoPaused(false)}
						/>
					) : (
						<IconButton
							variant='inline'
							aria-label="Pause Video"
							icon={<FontAwesomeIcon icon={faPause} />}
							onClick={() => setVideoPaused(true)}
						/>
					)}
					<Text textAlign='center' opacity={.5} flex={1}>
						{videoTime.toFixed(1)}s / {videoDuration.toFixed(1)}s
					</Text>
				</Center>
				<Slider value={videoTime} max={videoDuration} onChange={(e) => setVideoTime(e)}>
					<SliderTrack>
						<SliderFilledTrack />
					</SliderTrack>
					<SliderThumb />
				</Slider>
			</Center>
			<Center gap={10}>
				<Flex alignItems='center' gap={1} w={170}>
					<Text as='b'>Trim:</Text>
					<Box flex={1} />
					<IconButton aria-label="go to trim start" icon={<FontAwesomeIcon icon={faArrowLeft} />} onClick={() => setVideoTime(trimTime.start)} />
					<IconButton aria-label="go to trim start" icon={<FontAwesomeIcon icon={faArrowRight} />} onClick={() => setVideoTime(trimTime.end)} />
				</Flex>
				<RangeSlider aria-label={['start', 'end']} flex={1} step={.1} max={videoDuration} value={[trimTime.start, trimTime.end]} minStepsBetweenThumbs={5} onChange={(e) => {
					setTrimTime({ start: e[0], end: e[1] })
				}}>
					<RangeSliderTrack>
						<RangeSliderFilledTrack />
					</RangeSliderTrack>
					<RangeSliderThumb height={5} width={10} index={0}>
						<Text fontSize='sm' color='black'> {trimTime.start.toFixed(1)} </Text>
					</RangeSliderThumb>
					<RangeSliderThumb height={5} width={10} index={1}>
						<Text fontSize='sm' color='black'> {trimTime.end.toFixed(1)} </Text>
					</RangeSliderThumb>
				</RangeSlider>
			</Center>
			{trimTime.end - trimTime.start > 60 ? (
				<>
					<Box boxSize={3} />
					<Alert status='warning'>
						<AlertIcon />
						<AlertTitle>Long video clip!</AlertTitle>
						<AlertDescription>We reccomend keeping video clips under 60 seconds if possible</AlertDescription>
					</Alert>
				</>
			) : (<></>)}
			<Center gap={3} paddingY={3}>
				<ButtonGroup isAttached variant='outline'>
					<Button
						variant={mode === 'cover' ? 'solid' : 'outline'}
						colorScheme={mode === 'cover' ? 'blue' : 'gray'}
						onClick={() => setMode('cover')}
					>
						Crop to Fill
					</Button>
					<Button
						variant={mode === 'contain' ? 'solid' : 'outline'}
						colorScheme={mode === 'contain' ? 'blue' : 'gray'}
						onClick={() => setMode('contain')}
					>
						Fit Entire Video
					</Button>
				</ButtonGroup>
			</Center>
			<Center gap={3} paddingY={3}>
				<Text as='b' w={195}> Item Name: </Text>
				<Input
					placeholder="Item Name"
					value={newMedia.name}
					onChange={(e) => setNewMedia({ ...newMedia, name: e.target.value })}
				/>
			</Center>
			{!invalid ? (
				<Center paddingY={3} gap={3}>
					<Button flex={1} leftIcon={<FontAwesomeIcon icon={faUpload} />} onClick={() => {
						onSubmit({
							newMedia: newMedia,
							trim: trimTime,
							cropBox: { x: cropBox.x * clientScale, y: cropBox.y * clientScale, width: cropBox.width * clientScale, height: cropBox.height * clientScale },
							mode
						})
					}}>Upload Video</Button>
				</Center>
			) : (
				<Center paddingY={3} gap={3}>
					<Button variant='outline' colorScheme='red' flex={1} leftIcon={<FontAwesomeIcon icon={faCancel} />} disabled>{invalid}</Button>
				</Center>
			)}
		</Flex>
	)
}

export default VideoTrimmer
